import type { PendingCommands } from "./pending";
import React, { useEffect, useRef, useState } from "react";
import {
  ApiError,
  type Field,
  type Transport,
  type UploadReceipt,
} from "./transport";
import type { Copy } from "./copy";
/** A selection is not a clean upload. Only an explicit server-ready receipt supplies domain input. */
export function UploadField({
  field,
  api,
  pending,
  resource,
  c,
  disabled,
  value,
  onChange,
  prefix,
  onReceipt,
}: {
  field: Field;
  api: Transport;
  pending: PendingCommands;
  resource: { id: string; version: number };
  c: Copy;
  disabled: boolean;
  value: unknown;
  onChange: (value: unknown) => void;
  prefix: string;
  /** Current guarded receipt only: never an effect/authority callback. */
  onReceipt?: (receipt: UploadReceipt) => void;
}) {
  const [file, setFile] = useState<File>();
  const [receipt, setReceipt] = useState<UploadReceipt>();
  const [error, setError] = useState<ApiError>();
  const [busy, setBusy] = useState(false);
  const attempt = useRef("");
  const generation = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  const id = prefix + "-" + field.name;
  // While the scanner works, check again every few seconds (max ~2 minutes).
  useEffect(() => {
    if (receipt?.state !== "quarantined") return;
    const current = generation.current;
    let tries = 0, live = true;
    const timer = setInterval(async () => {
      if (!live || current !== generation.current || ++tries > 40) return clearInterval(timer);
      try {
        const updated = await api.uploadStatus(receipt.id);
        if (live && current === generation.current && updated.state !== "quarantined") {
          clearInterval(timer);
          result(updated);
        }
      } catch {
        /* keep the manual Status button as the fallback */
      }
    }, 3000);
    return () => { live = false; clearInterval(timer); };
  }, [receipt?.id, receipt?.state, api]);
  function result(receipt: UploadReceipt) {
    setReceipt(receipt);
    if (receipt.state === "ready") onChange(receipt.value);
    else onChange({ uploadPending: true });
    onReceipt?.(receipt);
  }
  async function upload() {
    if (!file || busy) return;
    setBusy(true);
    setError(undefined);
    const current = ++generation.current;
    try {
      const r = await pending.upload(
        file,
        resource,
        field.uploadPurpose!,
        attempt.current,
        field.maxBytes,
        prefix,
      );
      if (current === generation.current) result(r);
    } catch (e) {
      if (current === generation.current)
        setError(
          e instanceof ApiError ? e : new ApiError("error", "UPLOAD_FAILED"),
        );
    } finally {
      if (current === generation.current) setBusy(false);
    }
  }
  return (
    <div className="field upload-field" aria-busy={busy}>
      <label htmlFor={id}>
        {field.label}
        {field.required ? " *" : ""}
      </label>
      <input
        ref={input}
        id={id}
        type="file"
        accept={field.accept}
        disabled={disabled || busy || (!!error && error.state === "pending")}
        aria-describedby={id + "-status"}
        onChange={(e) => {
          generation.current++;
          const selected = e.target.files?.[0];
          if (
            selected &&
            (selected.size === 0 ||
              selected.size > (field.maxBytes ?? 10 * 1024 * 1024))
          ) {
            setError(new ApiError("error", "UPLOAD_SIZE_INVALID"));
            setFile(undefined);
            onChange("");
            e.target.value = "";
            return;
          }
          if (
            selected &&
            field.accept &&
            !field.accept.split(",").some((rule) => {
              const allowed = rule.trim().toLowerCase();
              return allowed.startsWith(".")
                ? selected.name.toLowerCase().endsWith(allowed)
                : allowed.endsWith("/*")
                  ? selected.type.toLowerCase().startsWith(allowed.slice(0, -1))
                  : selected.type.toLowerCase() === allowed;
            })
          ) {
            setError(new ApiError("error", "UPLOAD_TYPE_INVALID"));
            setFile(undefined);
            onChange("");
            e.target.value = "";
            return;
          }
          setFile(selected);
          attempt.current = crypto.randomUUID();
          setReceipt(undefined);
          setError(undefined);
          onChange(selected ? { uploadPending: true } : "");
        }}
      />
      <p id={id + "-status"} role="status">
        {busy
          ? c.pack["a11y.busy"]
          : error
            ? c.error + " · " + error.code
            : receipt?.state === "quarantined"
              ? c.pending
              : receipt?.state === "rejected"
                ? c.denied
                : receipt?.state === "ready" ||
                    (!!value && !(value as any).uploadPending)
                  ? c.success
                  : ""}
      </p>
      <div className="actions">
        {file && !receipt && (
          <button
            type="button"
            disabled={
              busy || disabled || (!!error && error.state === "pending")
            }
            onClick={() => void upload()}
          >
            {c.upload}
          </button>
        )}
        {receipt?.state === "quarantined" && (
          <button
            type="button"
            disabled={
              busy || disabled || (!!error && error.state === "pending")
            }
            onClick={async () => {
              setBusy(true);
              const current = ++generation.current;
              try {
                const updated = await api.uploadStatus(receipt.id);
                if (current === generation.current) result(updated);
              } catch (e) {
                if (current === generation.current) setError(e as ApiError);
              } finally {
                if (current === generation.current) setBusy(false);
              }
            }}
          >
            {c.status}
          </button>
        )}
        {(file || receipt) && (
          <button
            type="button"
            disabled={
              busy || disabled || (!!error && error.state === "pending")
            }
            onClick={() => {
              generation.current++;
              setFile(undefined);
              setReceipt(undefined);
              setError(undefined);
              onChange("");
              if (input.current) input.current.value = "";
            }}
          >
            {c.pack["confirmation.cancel"]}
          </button>
        )}
      </div>
    </div>
  );
}
